import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Mail, RefreshCw, Plus, Trash2, CheckCircle2, AlertCircle, Copy } from "lucide-react";
import { toast } from "sonner";

/**
 * R154 — Resend sender-domain verification.
 * Poll Resend Domains API, surface DNS records to add, trigger re-verify.
 */
export default function EmailDomainsPage() {
  const [items, setItems] = useState([]);
  const [currentFrom, setCurrentFrom] = useState("");
  const [currentDomain, setCurrentDomain] = useState("");
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.get("/admin/email/domains");
      if (data.error) {
        setError(data.error);
        setItems([]);
      } else {
        setItems(data.items || []);
      }
      setCurrentFrom(data.current_from_email || "");
      setCurrentDomain(data.current_from_domain || "");
    } catch (e) {
      setError(apiErrorMessage(e) || "Load failed");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const name = newName.trim().toLowerCase();
    if (!name || !name.includes(".")) return toast.error("Enter a domain (e.g. mail.prestigiouspm.net)");
    try {
      await api.post("/admin/email/domains", { name });
      toast.success("Domain added — copy the DNS records below into your registrar");
      setNewName("");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Add failed"); }
  };

  const verify = async (id) => {
    try {
      await api.post(`/admin/email/domains/${id}/verify`);
      toast.success("Verification triggered — status will refresh in ~30s");
      setTimeout(load, 30_000);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Verify failed"); }
  };

  const preflight = async (id) => {
    try {
      const { data } = await api.get(`/admin/email/domains/${id}/preflight`);
      const msg = `${data.matched_count}/${data.total} DNS records match on the wire${data.status ? ` · Resend status: ${data.status}` : ""}`;
      if (data.matched_count === data.total) toast.success(msg);
      else toast.warning(msg + " — check row highlights");
      // stash into the domain row so the table shows per-record diagnostics
      setItems((prev) => prev.map((d) => d.id === id ? { ...d, _preflight: data } : d));
    } catch (e) { toast.error(apiErrorMessage(e) || "Preflight failed"); }
  };

  const cfImport = async (id) => {
    if (!window.confirm("Push these DNS records into Cloudflare? Existing records with the same name+type will be OVERWRITTEN.")) return;
    try {
      const { data } = await api.post(`/admin/email/domains/${id}/cf-import`);
      toast.success(`Cloudflare synced — ${data.created} created · ${data.updated} updated`);
      setTimeout(() => preflight(id), 3000);
    } catch (e) { toast.error(apiErrorMessage(e) || "Cloudflare import failed"); }
  };

  const [cfAvailable, setCfAvailable] = useState(false);
  useEffect(() => {
    api.get("/admin/email/domains/cf/status")
      .then(({ data }) => setCfAvailable(data.cloudflare_available))
      .catch(() => setCfAvailable(false));
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this domain from Resend? Sending will fall back to the Emergent proxy.")) return;
    try {
      await api.delete(`/admin/email/domains/${id}`);
      toast.success("Removed");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Remove failed"); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  return (
    <div className="space-y-4" data-testid="email-domains-page">
      <PageHeader
        eyebrow="Email"
        title="Sender Domain Verification"
        subtitle="Verify a domain with Resend so estoppels + late notices land in inboxes instead of the Emergent proxy fallback."
      />

      <Card className="border-white/10 p-4 flex items-center gap-3">
        <Mail className="h-5 w-5 text-orange-400" />
        <div className="flex-1 text-sm">
          <div className="text-white/60 text-xs">Currently sending as</div>
          <div className="font-mono text-orange-200" data-testid="current-from-email">
            {currentFrom || <span className="text-white/40">not configured</span>}
          </div>
        </div>
        <Button variant="outline" onClick={load} disabled={busy}
                className="border-white/10 h-9" data-testid="email-refresh-btn">
          <RefreshCw className={`h-4 w-4 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </Card>

      {error && (
        <Card className="border-rose-500/40 bg-rose-500/[0.03] p-4 flex gap-3">
          <AlertCircle className="h-5 w-5 text-rose-400 flex-shrink-0" />
          <div>
            <div className="font-medium text-rose-200 mb-1">Resend API error</div>
            <div className="text-xs text-white/60 font-mono">{error}</div>
            <div className="text-xs text-white/60 mt-2">
              Add <code className="text-orange-300">RESEND_API_KEY</code> to <code>/app/backend/.env</code> and restart the backend.
            </div>
          </div>
        </Card>
      )}

      <Card className="border-white/10 p-4">
        <Label className="text-xs">Add a domain</Label>
        <div className="flex gap-2 mt-1">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                  placeholder="mail.prestigiouspm.net"
                  className="bg-white/[0.04] border-white/10 flex-1"
                  data-testid="new-domain-input" />
          <Button onClick={add} className="bg-orange-500 hover:bg-orange-600"
                  data-testid="add-domain-btn">
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
        <div className="text-xs text-white/50 mt-2">
          After adding, copy the SPF / DKIM / DMARC records into your registrar (Cloudflare / Namecheap / GoDaddy), wait ~10 minutes, then click <strong>Re-verify</strong>.
        </div>
      </Card>

      {items.map((d) => (
        <Card key={d.id} className="border-white/10 p-4" data-testid={`domain-${d.id}`}>
          <div className="flex items-center gap-2 mb-3">
            <div className="font-mono">{d.name}</div>
            <Badge variant="outline" className={`text-[10px] ${
              d.status === "verified" ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" :
              d.status === "pending" ? "border-amber-500/40 text-amber-300 bg-amber-500/10" :
              "border-rose-500/40 text-rose-300 bg-rose-500/10"
            }`}>{d.status}</Badge>
            {d.region && <span className="text-xs text-white/40">{d.region}</span>}
            <div className="ml-auto flex gap-2">
              {d.name === currentDomain && (
                <Badge variant="outline" className="text-[10px] border-orange-500/40 text-orange-300">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> RESEND_FROM matches
                </Badge>
              )}
              <button onClick={() => verify(d.id)}
                      className="text-xs px-2 py-1 rounded-md border border-orange-500/40 text-orange-300 hover:bg-orange-500/10"
                      data-testid={`domain-verify-${d.id}`}>
                Re-verify
              </button>
              <button onClick={() => preflight(d.id)}
                      className="text-xs px-2 py-1 rounded-md border border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                      data-testid={`domain-preflight-${d.id}`}>
                DNS Preflight
              </button>
              {cfAvailable && (
                <button onClick={() => cfImport(d.id)}
                        className="text-xs px-2 py-1 rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                        data-testid={`domain-cf-import-${d.id}`}>
                  Push to Cloudflare
                </button>
              )}
              <button onClick={() => remove(d.id)}
                      className="text-xs px-2 py-1 rounded-md border border-white/10 text-white/50 hover:border-rose-500/40 hover:text-rose-300">
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          </div>

          {(d.records || []).length > 0 ? (
            <div className="rounded-md border border-white/5 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-white/[0.03]">
                  <tr>
                    <th className="text-left px-3 py-2 text-white/60">Type</th>
                    <th className="text-left px-3 py-2 text-white/60">Name</th>
                    <th className="text-left px-3 py-2 text-white/60">Value</th>
                    <th className="text-left px-3 py-2 text-white/60">Status</th>
                    <th className="text-right px-3 py-2 text-white/60"></th>
                  </tr>
                </thead>
                <tbody>
                  {d.records.map((r, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="px-3 py-2 font-mono">{r.record || r.type}</td>
                      <td className="px-3 py-2 font-mono break-all">{r.name}</td>
                      <td className="px-3 py-2 font-mono break-all max-w-md truncate">{r.value}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={`text-[10px] ${
                          r.status === "verified" ? "border-emerald-500/40 text-emerald-300" :
                          "border-amber-500/40 text-amber-300"
                        }`}>{r.status || "pending"}</Badge>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => copy(r.value)}
                                className="text-white/40 hover:text-white">
                          <Copy className="h-3 w-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-xs text-white/50">No DNS records returned — try refreshing.</div>
          )}
        </Card>
      ))}
    </div>
  );
}
